import _ from 'lodash';
import {
  ASSIGNMENTS,
  CATEGORIES,
  developerEquipmentTypes,
  GROUPS,
  ownerEquipmentTypes,
} from '../constants/operations';
import { getRelativeYearIndex } from './farmData';
import { YEARLY_METRIC_SUFFIX } from './columns';
import { safeDivide, sumByIndex, thirtyYearArray } from './utils';
import { ANNUAL_MULTI_YEAR, MULTI_YEAR } from '../constants/years';

export const getOperationEquipmentType = ({
  assignment,
  contracted,
  rented,
}) => {
  const equipmentTypes =
    assignment === ASSIGNMENTS.OWNER
      ? ownerEquipmentTypes
      : developerEquipmentTypes;
  return contracted
    ? equipmentTypes.CONTRACTED
    : rented
    ? equipmentTypes.RENTED
    : equipmentTypes.DIRECT_OPERATED;
};

export const isDirectOperated = ({ contracted, rented }) =>
  !contracted && !rented;

export const occursDuringYearFilter = (item, year) =>
  !!item.occursDuringYearByYear[year];

const filterOperationsByCategoryAndYear = (
  opAggregates, // List of opAggregates
  filterCategory, // Category to filter opAggregates
  filterYear, // Dropdown year filter (MULTI_YEAR / ANNUAL_MULTI_YEAR / index)
  years, // Number of years to include for MULTI_YEAR / ANNUAL_MULTI_YEAR
  filterForYear, // Filter function (opAggregate, year) => boolean
  startYear = null, // Calculate relative year indices across farms
) =>
  opAggregates
    // Filter by category
    .filter(
      ({ category }) => filterCategory == null || category === filterCategory,
    )
    // Create one operation per year of interest for ANNUAL_MULTI_YEAR and store
    // relevant year index in opAggregate
    .flatMap((opAggregate) =>
      (filterYear === ANNUAL_MULTI_YEAR
        ? [...Array(years).keys()]
        : filterYear === MULTI_YEAR
        ? [years]
        : [filterYear]
      ).map((yearIndex) => ({ ...opAggregate, yearIndex })),
    )
    // Filter operations by year
    .filter((opAggregate) =>
      // If cumulative multi-year, check every year up to years
      (filterYear === MULTI_YEAR
        ? [...Array(years).keys()]
        : // otherwise check for yearIndex only
          [opAggregate.yearIndex]
      ).some((yearIndex) =>
        filterForYear(
          opAggregate,
          getRelativeYearIndex(yearIndex, startYear, opAggregate.farmStartYear),
        ),
      ),
    );

// NB: This is currently only used in LaborView, and may not work correctly for
// metrics across multiple farms with different start years
export const getLaborItems = (
  filterCategory, // Category to filter opAggregates
  filterYear, // Dropdown year filter (MULTI_YEAR / ANNUAL_MULTI_YEAR / index)
  years, // Number of years to include for MULTI_YEAR / ANNUAL_MULTI_YEAR
  filterForYear, // Filter function (opAggregate, year) => boolean
  startYear = null, // Calculate relative year indices across farms
) => ({ operationAggregates }) =>
  filterOperationsByCategoryAndYear(
    operationAggregates,
    filterCategory,
    filterYear,
    years,
    filterForYear,
    startYear,
  ).map((opAggregate) => {
    const relativeYearIndex = getRelativeYearIndex(
      opAggregate.yearIndex,
      startYear,
      opAggregate.farmStartYear,
    );
    const relativeTotalHoursByYear = opAggregate.hoursOfLaborByYear.slice(
      0,
      relativeYearIndex,
    );
    return {
      ...opAggregate,
      displayLaborHours:
        filterYear !== MULTI_YEAR
          ? // Get hours for single year
            opAggregate.hoursOfLaborByYear[relativeYearIndex]
          : filterCategory === 'installation'
          ? // Sum hours for all years if installation
            _.sum(relativeTotalHoursByYear)
          : // Otherwise average years with nonzero hours
            safeDivide(
              _.sum(relativeTotalHoursByYear),
              relativeTotalHoursByYear.filter((h) => h).length,
            ),
    };
  });

export const getOperationItems = (
  filterCategory, // Category to filter opAggregates
  filterYear, // Dropdown year filter (MULTI_YEAR / ANNUAL_MULTI_YEAR / index)
  years, // Number of years to include for MULTI_YEAR / ANNUAL_MULTI_YEAR
  filterForYear, // Filter function (opAggregate, year) => boolean
  yearlyMetrics, // Metrics ending in byYear to transform for ANNUAL_MULTI_YEAR
  startYear = null, // Calculate relative year indices across farms
) => ({ operationAggregates }) =>
  _.sortBy(
    filterOperationsByCategoryAndYear(
      operationAggregates,
      filterCategory,
      filterYear,
      years,
      filterForYear,
      startYear,
    ),
    ['yearIndex', 'name'],
  )
    // Calculate yearly metrics and average/max costs
    .map((opAggregate) => {
      const relativeYearIndex = getRelativeYearIndex(
        opAggregate.yearIndex,
        startYear,
        opAggregate.farmStartYear,
      );
      const relativeTotalCostByYear = opAggregate.totalCostByYear.slice(
        0,
        relativeYearIndex,
      );
      return {
        ...opAggregate,
        key: `${opAggregate.id}-${relativeYearIndex}`,
        year: startYear + parseInt(relativeYearIndex, 10),
        ...yearlyMetrics.reduce(
          (result, { accessor }) =>
            Object.assign(result, {
              // for all ByYear metrics, selects just the relevant year from the input's value
              [accessor.replace(YEARLY_METRIC_SUFFIX, '')]: opAggregate[
                accessor
              ][relativeYearIndex],
            }),
          {},
        ),
        ...(filterYear === MULTI_YEAR
          ? {
              avgCost: safeDivide(
                _.sum(relativeTotalCostByYear),
                relativeTotalCostByYear.length,
              ),
              maxCost: Math.max(...relativeTotalCostByYear) || 0,
              totalCost: _.sum(relativeTotalCostByYear),
            }
          : {}),
      };
    });

export const toAggregatesMap = (aggregates) =>
  _.keyBy(
    aggregates.flatMap(({ operationAggregates }) => operationAggregates),
    'operation.id',
  );

export const mergeBasisAggregates = (data) => {
  const { cropAggregates, bufferedAggregates, quotedAggregates } = data;
  const bufferedAggregatesMap = toAggregatesMap(bufferedAggregates);
  const quotedAggregatesMap = toAggregatesMap(quotedAggregates);
  return {
    ...data,
    cropAggregates: cropAggregates.map((crop) => ({
      ...crop,
      operationAggregates: crop.operationAggregates.map(
        (operationAggregate) => {
          const opId = operationAggregate.operation.id;
          const bufferedAggregate = bufferedAggregatesMap[opId];
          const quotedAggregate = quotedAggregatesMap[opId];
          return {
            ...operationAggregate,
            bufferedTotalCostByYear: bufferedAggregate?.totalCostByYear,
            quotedTotalCostByYear: quotedAggregate?.totalCostByYear,
          };
        },
      ),
    })),
  };
};

const sortByAccessorOptions = (options, accessor) => (operation) => {
  const index = options.indexOf(operation[accessor]);
  // If the accessor value does not match any of the options, sort it last
  return index === -1 ? options.length : index;
};

export const sortOperations = (operations) =>
  _.sortBy(operations, [
    sortByAccessorOptions(CATEGORIES, 'category'),
    sortByAccessorOptions(GROUPS, 'group'),
    'name',
  ]);

export const toOperationsTableData = ({ cropAggregates, startYear }) => ({
  cropAggregates: _.sortBy(cropAggregates, 'crop.name').map(
    ({ crop, operationAggregates }) => ({
      crop,
      operationAggregates: sortOperations(
        operationAggregates.map(({ operation, ...operationAggregate }) => ({
          ...operation,
          ...operationAggregate,
          crop,
          farmStartYear: startYear,
        })),
      ),
    }),
  ),
});

/*
Aggregates a yearly operations metric for each crop and across crops. Returns
a format suitable for MetricsSummary.
*/
export const getOperationsSummary = (dataAccessor, sumAccessor) => ({
  cropAggregates,
}) =>
  cropAggregates.reduce(
    (result, { crop, operationAggregates }) => {
      const aggregate = sumByIndex(
        operationAggregates.map((op) => op[dataAccessor]),
      );
      return Object.assign(result, {
        [crop.name]: aggregate,
        [sumAccessor]: result[sumAccessor].map(
          (sum, i) => (aggregate[i] || 0) + sum,
        ),
      });
    },
    { [sumAccessor]: thirtyYearArray(0) },
  );
